import React from 'react'
import { FormItem, FormItemProps } from './FormItem'
import {
  IField, getRequiredError, getMaxLengthError, getMinLengthError,
} from './FieldValidation'

export interface MultiLineFieldCallback { (value: string): any }

export interface MultiLineFieldProps extends FormItemProps {
    id: string
    onChange: MultiLineFieldCallback
    value: string
    rowNumber?: number
    minLength?: number
    maxLength?: number
    required?: boolean
    requiredMessage?: string
}

export interface MultiLineFieldState {
    value: string
    errorMessage?: string,
    counterText?: string,
    minLength?: number
}

const debounce = (callback: Function, delay: number) => {
  let timer: ReturnType<typeof setTimeout>
  return (...args: any) => {
    clearTimeout(timer)
    timer = setTimeout(() => callback(...args), delay)
  }
}

export class MultiLineField extends React.Component<MultiLineFieldProps, MultiLineFieldState> implements IField {
  input: HTMLTextAreaElement

  constructor(props?: MultiLineFieldProps, context?: any) {
    super(props, context)

    this.state = {
      value: this.props.value || '',
      minLength: this.props.required ? (this.props.minLength ?? 5) : null,
    }
  }

  debounceState = debounce((v: string) => {
    this.onChange(v)
  }, 1000)

  componentDidUpdate(prevProps: MultiLineFieldProps, prevState: MultiLineFieldState) {
    if (this.state.value == prevState.value && this.state.value != (this.props.value || '')) {
      this.setState({ value: this.props.value || '' }, () => {
        if (this.state.errorMessage) {
          this.validate(() => { })
        }
      })
    } else if (this.props.value != prevProps.value) {
      if (this.state.errorMessage) {
        this.validate(() => { })
      }
    }
  }

  onInput(v: string) {
    const cleanStr = this.checkForNonAllowedChar(v)
    this.setState({ value: cleanStr })
    this.setCounterText(cleanStr)
    this.debounceState(cleanStr)
  }

  onChange(v: string) {
    this.props.onChange(v)
  }

  render() {
    if (this.props.preview) {
      return (
        <>
          <FormItem {...this.props} errorMessage={this.props.errorMessage || this.state.errorMessage}>
            <div className="form-value">{this.state.value ? this.state.value : '--'}</div>
          </FormItem>
          {this.props.description && (
          <FormItem label={(
            <span className="form-content-preview" style={{ fontSize: this.props.preview ? '10pt' : 'inherits' }}>
              {this.props.description}
            </span>
)}
          />
          )}
        </>
      )
    }

    return (
      <FormItem {...this.props} inputId={this.props.id} errorMessage={this.props.errorMessage || this.state.errorMessage}>
        <textarea
          className={'emdg-text-input emdg-text-input--block'
                + `${(this.props.errorMessage || this.state.errorMessage) ? ' emdg-text-input--invalid' : ''}`}
          ref={(r) => this.input = r}
          id={this.props.id}
          value={this.state.value}
          onChange={(e) => this.onInput(e.target.value)}
          onBlur={(e) => this.onChange(e.target.value)}
          rows={this.props.rowNumber}
          minLength={this.state.minLength}
          maxLength={this.props.maxLength}
        />
        {this.state.counterText && <span>{this.state.counterText}</span>}
      </FormItem>
    )
  }

  checkForNonAllowedChar(v: string): string {
    const onlyAllowCharacters = /[^a-zA-Z !"#$%&'()*+,-./:;<=>?@\[\]\^_`{}|~0-9\n]/g
    const cleanStr = v.replace(onlyAllowCharacters, '')
    return cleanStr
  }

  setCounterText(val:string) : void {
    const { minLength } = this.state
    const { maxLength } = this.props

    if (minLength && val.length < minLength) {
      const charactersRequired = minLength - val.length
      const counterText = charactersRequired === 1 ? `${charactersRequired} minimum character required` : `${charactersRequired} minimum characters required`
      this.setState({ counterText })
    } else if (maxLength && val.length < maxLength) {
      const charactersLeft = maxLength - val.length
      const counterText = charactersLeft === 1 ? `${charactersLeft} character left` : `${charactersLeft} characters left`
      this.setState({ counterText })
    } else {
      this.setState({ counterText: '' })
    }
  }

  getValue(): string {
    let v = this.props.value
    if (!v) {
      v = ''
    }
    v.trim()
    return v
  }

  hasValue(): boolean {
    const v = this.getValue()
    return (v.length > 0)
  }

  focus() {
    this.input.focus()
  }

  getErrorText(): string {
    const val = this.getValue()
    if (this.props.required === true) {
      if (val.length == 0) {
        return getRequiredError(this.props)
      }
    }
    if (this.state.minLength) {
      if (val.length < this.state.minLength) {
        return getMinLengthError(this.props, this.state.minLength)
      }
    }
    if (this.props.maxLength) {
      if (val.length > this.props.maxLength) {
        return getMaxLengthError(this.props)
      }
    }
    return null
  }

  validate(callback: { (res: boolean): any }, focus?: boolean) {
    const errorText = this.getErrorText()
    this.setError(errorText, () => {
      if (this.props.errorMessage || errorText) {
        if (focus === true) {
          this.focus()
        }
      }
      callback && callback(!((this.props.errorMessage || errorText)))
    })
  }

  setError(errorMessage?: string, callback?: { (): any }) {
    this.setState({ errorMessage }, callback)
  }
}
